import React, { useRef, useState } from 'react';
import Modal from '../../../components/Modals/Modal';
import { Container, Row } from '../../../components/commons';
import Button from '../../../components/Button';
import useToast from '../../../hooks/useToast';
import { useMunicipiosTce, useProvisionWizard } from '../../../services/apiIntegrations.service';

export interface ProvisionWizardModalHandle {
  openModal: () => void;
}

type ModalRef = React.ElementRef<typeof Modal>;

const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const ProvisionWizardModal: React.ForwardRefRenderFunction<ProvisionWizardModalHandle, { reload?: () => void }> = (
  { reload },
  ref
) => {
  const modalRef = useRef<ModalRef>(null);
  const toast = useToast();

  const [codigoMunicipio, setCodigoMunicipio] = useState('072');
  const [nomeMunicipio, setNomeMunicipio] = useState('ICAPUÍ');
  const [anoInicio, setAnoInicio] = useState(new Date().getFullYear());
  const [anoFim, setAnoFim] = useState(new Date().getFullYear());
  const [criarPastas, setCriarPastas] = useState(true);
  const [criarTipos, setCriarTipos] = useState(true);
  const [configurarAutoFill, setConfigurarAutoFill] = useState(true);
  const [sincronizarTce, setSincronizarTce] = useState(false);

  const { data: municipios, isLoading: loadingMunicipios } = useMunicipiosTce();
  const provisionMutation = useProvisionWizard();

  React.useImperativeHandle(ref, () => ({
    openModal: () => {
      modalRef.current?.openModal();
    },
  }));

  const handleMunicipioChange = (cod: string) => {
    setCodigoMunicipio(cod);
    const found = municipios?.find((m) => m.codigo === cod);
    if (found) {
      setNomeMunicipio(found.nome);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (anoInicio > anoFim) {
      toast.errorToast('Ano inicial não pode ser maior que o ano final.');
      return;
    }

    const anos: number[] = [];
    for (let a = anoInicio; a <= anoFim; a++) {
      anos.push(a);
    }

    try {
      const response = await provisionMutation.mutateAsync({
        codigoMunicipio,
        nomeMunicipio,
        anos,
        criarPastas,
        criarTiposDocumentos: criarTipos,
        configurarAutoFill,
        sincronizarTce,
      });

      toast.successToast(
        response?.message || `Provisão de ${nomeMunicipio} (${anos.join(', ')}) concluída!`
      );
      if (reload) reload();
      modalRef.current?.closeModal();
    } catch (error: any) {
      toast.errorToast(
        error.response?.data?.message || 'Erro ao executar o assistente de provisão.'
      );
    }
  };

  return (
    <Modal ref={modalRef} title='⚡ Assistente de Provisão em 1 Clique (TCE-CE / SIM)'>
      <Container>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            fontFamily: fontStack,
          }}
        >
          {/* Banner Informativo */}
          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: 8,
              padding: '12px 16px',
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#14532d',
                display: 'block',
                marginBottom: 4,
                fontFamily: fontStack,
              }}
            >
              Padronização Automática de Acervos Municipais
            </span>
            <p
              style={{
                margin: 0,
                fontSize: 12.5,
                color: '#166534',
                lineHeight: 1.45,
                fontFamily: fontStack,
              }}
            >
              Gera automaticamente os tipos <strong>${'{PREF}'} - PROCESSO CONTÁBIL - ${'{ANO}'}</strong> e{' '}
              <strong>${'{PREF}'} - PROCESSO LICITATÓRIO - ${'{ANO}'}</strong>, cria as pastas no gerenciador de arquivos e
              amarra as regras de auto-indexação em 1 único clique.
            </p>
          </div>

          {/* Seleção do Município */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                color: '#0f172a',
                marginBottom: 6,
                fontFamily: fontStack,
              }}
            >
              Município (Prefeitura)
            </label>
            <select
              value={codigoMunicipio}
              onChange={(e) => handleMunicipioChange(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontSize: 13.5,
                color: '#0f172a',
                backgroundColor: '#ffffff',
                fontFamily: fontStack,
                outline: 'none',
              }}
            >
              {loadingMunicipios ? (
                <option value='072'>Carregando municípios do TCE-CE...</option>
              ) : municipios && municipios.length > 0 ? (
                municipios.map((m: any) => (
                  <option key={m.codigo} value={m.codigo}>
                    {m.codigo} - {m.nome}
                  </option>
                ))
              ) : (
                <>
                  <option value='072'>072 - ICAPUÍ</option>
                  <option value='005'>005 - ACOPIARA</option>
                  <option value='104'>104 - MAURITI</option>
                  <option value='169'>169 - TIANGUÁ</option>
                </>
              )}
            </select>
          </div>

          {/* Anos */}
          <Row justify='space-between' gap={12}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: 6,
                  fontFamily: fontStack,
                }}
              >
                Ano Inicial
              </label>
              <input
                type='number'
                min={2000}
                max={2100}
                value={anoInicio}
                onChange={(e) => setAnoInicio(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: 13.5,
                  color: '#0f172a',
                  backgroundColor: '#ffffff',
                  fontFamily: fontStack,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: 6,
                  fontFamily: fontStack,
                }}
              >
                Ano Final
              </label>
              <input
                type='number'
                min={2000}
                max={2100}
                value={anoFim}
                onChange={(e) => setAnoFim(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: 13.5,
                  color: '#0f172a',
                  backgroundColor: '#ffffff',
                  fontFamily: fontStack,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </Row>

          {/* Ações Automáticas com Cards Legíveis */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '14px 16px',
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#0f172a',
                display: 'block',
                marginBottom: 10,
                fontFamily: fontStack,
              }}
            >
              Ações Automáticas:
            </span>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Opção 1 */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  cursor: 'pointer',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  padding: '11px 14px',
                }}
              >
                <input
                  type='checkbox'
                  checked={criarTipos}
                  onChange={(e) => setCriarTipos(e.target.checked)}
                  style={{ marginTop: 2, cursor: 'pointer', accentColor: '#2563eb', width: 16, height: 16 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', fontFamily: fontStack }}>
                    Aproveitar / Criar Tipos de Documentos Padrão
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', fontFamily: fontStack, marginTop: 2 }}>
                    Reconhece o padrão existente (Nº do Processo / Nº de Caixa, Credor, Empenho, Objeto) sem duplicar
                  </span>
                </div>
              </label>

              {/* Opção 2 */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  cursor: 'pointer',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  padding: '11px 14px',
                }}
              >
                <input
                  type='checkbox'
                  checked={criarPastas}
                  onChange={(e) => setCriarPastas(e.target.checked)}
                  style={{ marginTop: 2, cursor: 'pointer', accentColor: '#2563eb', width: 16, height: 16 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', fontFamily: fontStack }}>
                    Criar Estrutura de Pastas no Armazenamento
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', fontFamily: fontStack, marginTop: 2 }}>
                    Cria /{nomeMunicipio}/{anoInicio}.../PROCESSOS CONTÁBEIS e PROCESSOS LICITATÓRIOS
                  </span>
                </div>
              </label>

              {/* Opção 3 */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  cursor: 'pointer',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  padding: '11px 14px',
                }}
              >
                <input
                  type='checkbox'
                  checked={configurarAutoFill}
                  onChange={(e) => setConfigurarAutoFill(e.target.checked)}
                  style={{ marginTop: 2, cursor: 'pointer', accentColor: '#2563eb', width: 16, height: 16 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', fontFamily: fontStack }}>
                    Amarrar Regras de Indexação Automática (AutoFill)
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', fontFamily: fontStack, marginTop: 2 }}>
                    Preenchimento automático em 2 passos por Doc_Caixa + Ano_Empenho e por N_Proc
                  </span>
                </div>
              </label>

              {/* Opção 4: Sincronização Imediata */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  cursor: 'pointer',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: 6,
                  padding: '11px 14px',
                }}
              >
                <input
                  type='checkbox'
                  checked={sincronizarTce}
                  onChange={(e) => setSincronizarTce(e.target.checked)}
                  style={{ marginTop: 2, cursor: 'pointer', accentColor: '#d97706', width: 16, height: 16 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: '#78350f', fontFamily: fontStack }}>
                    Baixar dados do TCE-CE em background imediatamente
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e', fontFamily: fontStack, marginTop: 2 }}>
                    Inicia a ingestão de pagamentos, empenhos e licitações para os anos selecionados
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div style={{ marginTop: 6 }}>
            <Button
              label={provisionMutation.isLoading ? 'Provisionando Acervo...' : '⚡ Executar Provisão em 1 Clique'}
              type='submit'
              color='primary'
              variantType='block'
              loading={provisionMutation.isLoading}
            />
          </div>
        </form>
      </Container>
    </Modal>
  );
};

export default React.forwardRef(ProvisionWizardModal);
